import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid2,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Link, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import performRequest from "network/perform-request";
import { competitionsDashboardRoute } from "network/api-routes";
import UserContext from "context/UserContext";
import {
  adminEventRoute,
  adminEventTournamentRoute,
  adminEventTournamentsRoute,
  publicEventRoute,
} from "constants/routes";
import {
  formatRegistrationAmount,
  groupTournamentsByOperationalState,
  registrationSearch,
  tournamentActionMessageId,
} from "./adminEventUtils";

const DashboardKpi = ({ icon, label, value, to }) => (
  <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
    <Card
      component={Link}
      to={to}
      variant="outlined"
      sx={{
        color: "inherit",
        display: "block",
        height: "100%",
        textDecoration: "none",
        transition: "border-color 150ms ease, transform 150ms ease",
        "&:hover": { borderColor: "primary.main", transform: "translateY(-2px)" },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
            <Box>
              <Typography variant="h4">{value}</Typography>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </Box>
          </Stack>
          <ChevronRightOutlinedIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  </Grid2>
);

const AttentionItem = ({ icon, title, description, action, to, color = "warning" }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ sm: "center" }}
    justifyContent="space-between"
    gap={1.5}
    sx={{ py: 1.5 }}
  >
    <Stack direction="row" gap={1.5} alignItems="flex-start">
      <Box sx={{ color: `${color}.main`, display: "flex", mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
    <Button
      component={Link}
      to={to}
      endIcon={<ArrowForwardOutlinedIcon />}
      sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
    >
      {action}
    </Button>
  </Stack>
);

const Competition = () => {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await performRequest("POST", competitionsDashboardRoute, { competition_uuid: id }, true, user);
      setDashboard(response.data.data);
    } catch (requestError) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatNumber = (value) => intl.formatNumber(value ?? 0);
  const formatAmount = (value, currencyCode) => formatRegistrationAmount(value, currencyCode, intl.locale);

  const setupSteps = useMemo(() => {
    if (!dashboard) return [];
    const { event } = dashboard;
    const eventRoute = adminEventRoute(id);
    return [
      {
        label: "Configurar banner",
        descriptionId: "overview.setup.bannerDescription",
        completed: Boolean(event.full_banner_url || event.full_cover_url),
        to: `${eventRoute}/edit`,
      },
      {
        label: "Crear un torneo",
        descriptionId: "overview.setup.tournamentDescription",
        completed: event.tournaments_count > 0,
        to: `${eventRoute}/tournaments/create`,
      },
      {
        label: "Agregar una sede",
        descriptionId: "overview.setup.venueDescription",
        completed: event.establishments_count > 0,
        to: `${eventRoute}/clubs`,
      },
      {
        label: "Configurar cuenta",
        descriptionId: "overview.setup.accountDescription",
        completed: event.account_available,
        to: `${eventRoute}/edit`,
      },
      {
        label: "Publicar evento",
        descriptionId: "overview.setup.publishDescription",
        completed: event.published,
        to: `${eventRoute}/edit`,
      },
    ];
  }, [dashboard, id]);

  if (loading) return <Skeleton variant="rounded" height={460} />;
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" startIcon={<RefreshOutlinedIcon />} onClick={fetchDashboard}>
            {intl.formatMessage({ id: "Reintentar" })}
          </Button>
        }
      >
        {intl.formatMessage({ id: "No se pudo cargar el resumen del evento." })}
      </Alert>
    );
  }
  if (!dashboard) return null;

  const { event, metrics, tournaments, recent_registrations: recentRegistrations } = dashboard;
  const noTournaments = tournaments.length === 0;
  const incompleteSteps = setupSteps.filter((step) => !step.completed);
  const tournamentGroups = groupTournamentsByOperationalState(tournaments);
  const registrationsRoute = `${adminEventRoute(id)}/registrations`;
  const registrationUrl = (filters = {}, registrationId = null) =>
    `${registrationsRoute}${registrationSearch(filters, registrationId)}`;
  const attentionItems = [
    ...tournaments
      .filter((tournament) => tournament.state === "pending-finished")
      .map((tournament) => ({
        key: `finish-${tournament.id}`,
        icon: <WarningAmberOutlinedIcon />,
        title: tournament.name,
        description: intl.formatMessage({ id: "overview.attention.finishTournamentDescription" }),
        action: intl.formatMessage({ id: "overview.actions.reviewAndFinish" }),
        to: adminEventTournamentRoute(id, tournament.uuid_),
        color: "warning",
      })),
    ...tournaments
      .filter((tournament) => tournament.state === "final-stage-pending")
      .map((tournament) => ({
        key: `final-stage-${tournament.id}`,
        icon: <WarningAmberOutlinedIcon />,
        title: tournament.name,
        description: intl.formatMessage({ id: "overview.attention.startFinalStageDescription" }),
        action: intl.formatMessage({ id: "overview.actions.startFinalStage" }),
        to: adminEventTournamentRoute(id, tournament.uuid_),
        color: "info",
      })),
    ...(metrics.pending_registrations_count > 0
      ? [
          {
            key: "pending-payments",
            icon: <PaymentsOutlinedIcon />,
            title: intl.formatMessage(
              { id: "{count} pagos pendientes" },
              { count: metrics.pending_registrations_count },
            ),
            description: intl.formatMessage({ id: "overview.attention.pendingPaymentsDescription" }),
            action: intl.formatMessage({ id: "overview.actions.reviewPayments" }),
            to: registrationUrl({ status: "waiting-payment" }),
            color: "warning",
          },
        ]
      : []),
    ...incompleteSteps.map((step) => ({
      key: `setup-${step.label}`,
      icon: <SettingsOutlinedIcon />,
      title: intl.formatMessage({ id: step.label }),
      description: intl.formatMessage({ id: step.descriptionId }),
      action: intl.formatMessage({ id: "overview.actions.complete" }),
      to: step.to,
      color: "primary",
    })),
  ];

  const registrationStatus = (status) =>
    ({
      completed: { labelId: "Confirmada", color: "success" },
      "waiting-payment": { labelId: "Pendiente", color: "warning" },
      expired: { labelId: "Expirada", color: "default" },
    })[status] || { labelId: status, color: "default" };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        alignItems={{ md: "center" }}
      >
        <Box>
          <Typography component="h1" variant="h2">
            {event.name}
          </Typography>
          <Typography color="text.secondary">
            {event.start_at
              ? intl.formatDate(new Date(event.start_at), { day: "numeric", month: "long", year: "numeric" })
              : "—"}
            {event.end_at
              ? ` — ${intl.formatDate(new Date(event.end_at), { day: "numeric", month: "long", year: "numeric" })}`
              : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={intl.formatMessage({ id: event.published ? "Publicado" : "Borrador" })}
            color={event.published ? "success" : "default"}
          />
          {event.published && (
            <Button
              component={Link}
              to={`${publicEventRoute(event.uuid_)}`}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewOutlinedIcon />}
            >
              {intl.formatMessage({ id: "Ver página pública" })}
            </Button>
          )}
        </Stack>
      </Stack>

      {noTournaments ? (
        <Paper sx={{ p: { xs: 3, md: 6 }, textAlign: "center" }}>
          <Box component="img" src="/img/welcome.svg" alt="" sx={{ width: "100%", maxWidth: 260 }} />
          <Typography variant="h4" sx={{ mt: 2 }}>
            {intl.formatMessage({ id: "event.ready" }, { eventName: event.name })}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {intl.formatMessage({ id: "Crea tu primer torneo para comenzar a gestionar el evento." })}
          </Typography>
          <Button
            component={Link}
            to={`${adminEventTournamentsRoute(id)}/create`}
            variant="contained"
            size="large"
            startIcon={<AddCircleOutlineIcon />}
          >
            {intl.formatMessage({ id: "Crear un torneo" })}
          </Button>
        </Paper>
      ) : (
        <>
          <Grid2 container spacing={2}>
            <DashboardKpi
              icon={<PeopleOutlineIcon />}
              label={intl.formatMessage({ id: "Participantes" })}
              value={formatNumber(metrics.participants_count)}
              to={registrationUrl()}
            />
            <DashboardKpi
              icon={<FactCheckOutlinedIcon />}
              label={intl.formatMessage({ id: "Inscripciones confirmadas" })}
              value={formatNumber(metrics.completed_registrations_count)}
              to={registrationUrl({ status: "completed" })}
            />
            <DashboardKpi
              icon={<PendingActionsOutlinedIcon />}
              label={intl.formatMessage({ id: "Pagos pendientes" })}
              value={formatNumber(metrics.pending_registrations_count)}
              to={registrationUrl({ status: "waiting-payment" })}
            />
            <DashboardKpi
              icon={<PaidOutlinedIcon />}
              label={intl.formatMessage({ id: "Monto confirmado de inscripciones" })}
              value={formatAmount(metrics.confirmed_signup_amount, metrics.currency_code)}
              to={registrationUrl({ status: "completed" })}
            />
          </Grid2>

          <Typography color="text.secondary" variant="body2">
            {intl.formatMessage(
              { id: "Desglose: {online} online, {manual} manual y {expired} expiradas." },
              {
                online: formatNumber(metrics.online_registrations_count),
                manual: formatNumber(metrics.manual_registrations_count),
                expired: formatNumber(metrics.expired_registrations_count),
              },
            )}
          </Typography>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" component="h2">
              {intl.formatMessage({ id: "overview.attention.title" })}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              {intl.formatMessage({ id: "overview.attention.subtitle" })}
            </Typography>
            {attentionItems.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mt: 2 }}>
                {intl.formatMessage({ id: "overview.attention.allCaughtUp" })}
              </Alert>
            ) : (
              <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
                {attentionItems.map((item) => (
                  <AttentionItem key={item.key} {...item} />
                ))}
              </Stack>
            )}
          </Paper>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, lg: 7 }}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography component="h2" variant="h5">
                    {intl.formatMessage({ id: "overview.tournaments.title" })}
                  </Typography>
                  <Button component={Link} to={adminEventTournamentsRoute(id)}>
                    {intl.formatMessage({ id: "Ver todos" })}
                  </Button>
                </Stack>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                  {intl.formatMessage({ id: "overview.tournaments.subtitle" })}
                </Typography>
                <Stack spacing={1}>
                  {tournamentGroups.map((group) => (
                    <Accordion
                      key={group.key}
                      defaultExpanded={group.defaultExpanded}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%", pr: 1 }}>
                          <Typography fontWeight={700} sx={{ flexGrow: 1 }}>
                            {intl.formatMessage({ id: group.titleId })}
                          </Typography>
                          <Chip size="small" color={group.color} label={intl.formatNumber(group.tournaments.length)} />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {intl.formatMessage({ id: group.descriptionId })}
                        </Typography>
                        <Stack divider={<Divider flexItem />}>
                          {group.tournaments.map((tournament) => (
                            <Stack
                              key={tournament.id}
                              direction={{ xs: "column", sm: "row" }}
                              justifyContent="space-between"
                              alignItems={{ sm: "center" }}
                              gap={1}
                              sx={{ py: 1.25 }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={700} noWrap>
                                  {tournament.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {tournament.start_at
                                    ? intl.formatDate(new Date(tournament.start_at), {
                                        day: "2-digit",
                                        month: "short",
                                      })
                                    : "—"}{" "}
                                  ·{" "}
                                  {intl.formatMessage(
                                    { id: "overview.tournaments.participantCount" },
                                    { count: tournament.participants_count },
                                  )}
                                  {tournament.signup_cap
                                    ? ` / ${intl.formatNumber(tournament.signup_cap)}`
                                    : ""}
                                </Typography>
                              </Box>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {tournament.state === "pending" && (
                                  <Chip
                                    size="small"
                                    label={intl.formatMessage({
                                      id: tournament.open_signup
                                        ? "Inscripciones abiertas"
                                        : "Inscripciones cerradas",
                                    })}
                                    color={tournament.open_signup ? "success" : "default"}
                                  />
                                )}
                                <Button
                                  component={Link}
                                  to={adminEventTournamentRoute(id, tournament.uuid_)}
                                  size="small"
                                >
                                  {intl.formatMessage({ id: tournamentActionMessageId(tournament.state) })}
                                </Button>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Paper>
            </Grid2>
            <Grid2 size={{ xs: 12, lg: 5 }}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography component="h2" variant="h5">
                    {intl.formatMessage({ id: "overview.registrations.title" })}
                  </Typography>
                  <Button component={Link} to={registrationsRoute}>
                    {intl.formatMessage({ id: "Ver todas" })}
                  </Button>
                </Stack>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                  {intl.formatMessage({ id: "overview.registrations.subtitle" })}
                </Typography>
                {recentRegistrations.length === 0 ? (
                  <Typography color="text.secondary">
                    {intl.formatMessage({ id: "Aún no hay inscripciones en este evento." })}
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />}>
                    {recentRegistrations.map((registration) => {
                      const status = registrationStatus(registration.status);

                      return (
                        <Box
                          key={registration.id}
                          component={Link}
                          to={registrationUrl({}, registration.id)}
                          sx={{
                            color: "inherit",
                            display: "block",
                            mx: -1,
                            px: 1,
                            py: 1.25,
                            textDecoration: "none",
                            borderRadius: 1,
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={700} noWrap>
                                {registration.participant?.name || intl.formatMessage({ id: "Desconocido" })}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {registration.tournament?.name || "—"}
                              </Typography>
                              <Stack
                                direction="row"
                                gap={0.75}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.75 }}
                              >
                                <Chip
                                  size="small"
                                  label={intl.formatMessage({ id: status.labelId })}
                                  color={status.color}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={intl.formatMessage({
                                    id:
                                      registration.origin === "manual"
                                        ? "overview.registrations.adminOrigin"
                                        : "overview.registrations.selfServiceOrigin",
                                  })}
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {registration.created_at
                                    ? intl.formatDate(new Date(registration.created_at), {
                                        day: "2-digit",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "—"}
                                </Typography>
                              </Stack>
                            </Box>
                            <ChevronRightOutlinedIcon color="action" />
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Grid2>
          </Grid2>
        </>
      )}
    </Stack>
  );
};

export default Competition;
